import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Loader2, WifiOff } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { sectionLabel } from "@/lib/sections";
import { gazaDateTime } from "@/lib/dateGaza";

/** One search field with results appearing as you type (title + content). `compact` = header dropdown; otherwise inline panel. */
export default function LiveSearch({ compact = false, initialQ = "", onQueryChange, testId = "live-search", autoFocus = false }) {
    const { t, p, lang, dir } = useLang();
    const [q, setQ] = useState(initialQ);
    const [items, setItems] = useState(null);
    const [state, setState] = useState("idle"); // idle | loading | done | error
    const [open, setOpen] = useState(false);
    const seq = useRef(0);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const term = q.trim();
        onQueryChange?.(term);
        if (!term) { seq.current += 1; setItems(null); setState("idle"); return; }
        const my = ++seq.current;
        setState("loading");
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get("/public/search", { params: { q: term, limit: 30 } });
                if (my !== seq.current) return;   // a newer term is already in flight — keep results matching the latest text
                setItems(data.items || []); setState("done");
            } catch {
                if (my !== seq.current) return;
                setState("error");
            }
        }, 250);
        return () => clearTimeout(h);
        // eslint-disable-next-line
    }, [q, lang]);

    useEffect(() => {
        if (!compact) return;
        const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
        document.addEventListener("pointerdown", onDoc);
        return () => document.removeEventListener("pointerdown", onDoc);
    }, [compact]);

    const showPanel = compact ? open && q.trim() : q.trim();
    const panel = showPanel ? (
        <div className={`live-search-panel ${compact ? "live-search-panel--float" : ""}`} role="listbox" data-testid={`${testId}-panel`} data-state={state}>
            {state === "loading" && items == null && <div className="live-search-state" data-testid={`${testId}-loading`}><Loader2 className="w-4 h-4 animate-spin" /> {t("searching")}</div>}
            {state === "error" && <div className="live-search-state" data-testid={`${testId}-error`}><WifiOff className="w-4 h-4" /> {t("searchFail")} <button type="button" className="underline ms-1" onClick={() => setQ((v) => v + "")}>{t("retry")}</button></div>}
            {state !== "error" && items && items.length === 0 && <div className="live-search-state" data-testid={`${testId}-empty`}>{t("noResults")}</div>}
            {items && items.length > 0 && (
                <ul className="live-search-list" data-testid={`${testId}-results`} aria-busy={state === "loading"}>
                    {items.map((a) => (
                        <li key={a.id} role="option" aria-selected="false">
                            <Link to={`${p}/article/${a.slug}`} className="live-search-item" onClick={() => setOpen(false)} data-testid={`${testId}-item-${a.slug}`}>
                                <span className="live-search-section">{sectionLabel(a.section_slug, lang)}</span>
                                <span className="live-search-title">{a.title}</span>
                                <time className="live-search-date" dateTime={a.published_at}>{gazaDateTime(a.published_at, lang)}</time>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    ) : null;

    return (
        <div ref={wrapRef} dir={dir} className={`live-search ${compact ? "relative" : ""}`} data-testid={testId}>
            <div className="live-search-field" data-filled={q ? "1" : "0"}>
                <Search className="live-search-icon w-4 h-4" aria-hidden="true" />
                <input ref={inputRef} type="search" value={q} autoFocus={autoFocus} enterKeyHint="search" autoComplete="off"
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
                    placeholder={t("searchPlaceholder")} aria-label={t("search")} className="live-search-input" data-testid={`${testId}-input`} />
                {q && <button type="button" onClick={() => { setQ(""); inputRef.current?.focus(); }} className="live-search-clear" aria-label={t("clearSearch")} data-testid={`${testId}-clear`}><X className="w-4 h-4" /></button>}
            </div>
            {panel}
        </div>
    );
}
